import { List } from 'immutable';
import { debounce, partial } from 'lodash';
import React, {
  useMemo,
  useState,
  useEffect,
  useLayoutEffect,
  useRef,
  useContext,
} from 'react';
import { Button } from '@vimily/bonjoro-ui-components';

import Tag from './SendTo/Tag';
import Context from '../Context';
import { isEmail } from '~/utils/String';
import FormValues from './SendTo/FormValues';
import SearchResults from '../SearchResults';
import { Step } from '~/App/Components/Typeform';
import InputHelpBlock from './SendTo/InputHelpBlock';
import TagsInput from '~/App/Components/Forms/TaggedInput';
import StepContext from '~/App/Components/Typeform/StepContext';
import useEventListener from '~/react-hooks/use-event-listener';
import ActionButton from '~/App/Components/Typeform/ActionButton';
import Alert from '~/App/Components/Alerts/Alert';
import styled from '@emotion/styled';
import { Profile } from '~/types/models';
import { TodoCreateMode } from '~/App/Modules/TodoList/Modals/TodoCreate';
import { useProfiles } from '~/App/Api/Profile';

const Icon = styled.i`
  color: #d0577d;
`;

const ButtonWrapper = styled.div`
  .action-button-wrapper {
    float: left;
  }

  > button {
    float: right;
  }

  &:before,
  &:after {
    display: block;
    content: '';
    clear: both;
    height: 0;
  }
`;

export interface SendToProps {
  contacts: object;
  contactCount: number;
  contactsArray: Array<Profile>;
  selectContact: (id: string, name: string, profile: Profile) => void;
  maxContacts: number;
  selectEmail: (email: string) => void;
  deselectContact: (key: string, label: string, data: string) => void;
  isContactSelected: (key: string) => boolean;
  hasReachedMaxContacts: boolean;
  unsubscribedContact: Profile | false;
  mode: string;
}

export default function SendTo({
  maxContacts,
  contactCount,
  contactsArray,
  selectContact,
  selectEmail,
  deselectContact,
  isContactSelected,
  hasReachedMaxContacts,
  unsubscribedContact,
  mode,
}: SendToProps) {
  const [query, setQuery] = useState('');
  //used to debounce the profile search
  const [searchQuery, setSearchQuery] = useState('');

  const [showAlert, setShowAlert] = useState(false);
  const inputRef = useRef<HTMLInputElement>();
  const outerRef = useRef();
  const { isCurrent, nextStep } = useContext(StepContext);
  const canGoToNextStep = mode != TodoCreateMode.GROUP;
  const actionText = mode == TodoCreateMode.GROUP ? 'Add' : undefined;
  const [isNextBtnActive, setIsNextBtnActive] = useState(false);
  const { data: profiles = [], isLoading } = useProfiles({
    search: searchQuery,
  });
  const updateSearchQuery = useMemo(() => debounce(setSearchQuery, 200), []);

  // maintain a list of keys of each type of focusable item
  // currently just profiles but groups in future
  const keys = useMemo(
    () =>
      List().withMutations((list) => {
        // profiles
        list.push(...profiles.map((profile) => `person_${profile.id}`));
      }),
    [profiles /* groups */]
  );

  const [focusIndex, setFocusIndex] = useState<number | null>(null);
  const keyIndex = focusIndex !== null ? focusIndex : 0;
  const focusKey = useMemo(() => keys.get(keyIndex), [keys, focusIndex]);

  const goToNextStep = () => debounce(nextStep, 150)(true);

  useEffect(() => {
    if (query != '') {
      setQuery('');
    }

    // refocus the input
    setFocusIndex(null);

    if (hasReachedMaxContacts) {
      debounce(nextStep, 150)(true);
    }
  }, [contactCount, hasReachedMaxContacts]);

  useEffect(() => {
    if (mode == TodoCreateMode.GROUP) {
      setIsNextBtnActive(!contactCount);
    }
  }, [contactCount, mode]);

  useEffect(() => {
    updateSearchQuery(query);
    setShowAlert(false);
    setFocusIndex(null);
  }, [query]);

  useEffect(() => {
    if (unsubscribedContact) {
      setShowAlert(true);
    }
  }, [unsubscribedContact]);

  const queryIsEmail = useMemo(() => isEmail(query), [query]);

  function validate({ key }: { key: string | null }) {
    if (key == null) {
      return false;
    }

    return !isContactSelected(key);
  }

  useLayoutEffect(() => {
    if (typeof focusIndex !== 'number') {
      if (inputRef.current) inputRef.current.focus();
    }
  }, [focusIndex, inputRef]);

  // prevent arrow scrolling
  useEventListener('keydown', (evt) => {
    if (['ArrowUp', 'ArrowDown'].includes(evt.code)) {
      evt.preventDefault();
    }
  });

  const context = useMemo(
    () => ({
      clearInput: partial(setQuery, ''),
      focusKey,
      nextMenuItem: () => {
        if (focusIndex == null) {
          setFocusIndex(0);
        } else if (focusIndex < keys.count() - 1) {
          setFocusIndex(focusIndex + 1);
        }
      },
      prevMenuItem: () =>
        focusIndex && focusIndex > 0
          ? setFocusIndex(focusIndex - 1)
          : setFocusIndex(null),
    }),
    [focusIndex]
  );

  const handleKeyEvent = debounce((evt) => {
    switch (evt.code) {
      case 'ArrowDown':
        context.nextMenuItem();
        break;
      case 'ArrowUp':
        context.prevMenuItem();
    }
  }, 100);

  useEventListener('keydown', handleKeyEvent, outerRef);

  // focuses the input when user clicks a contact
  function handleContactAction(fn: (...props) => void, ...args) {
    fn(...args);
  }

  return (
    <Context.Provider value={context}>
      <Step heading='Send To' ref={outerRef}>
        <TagsInput
          tabIndex={-1}
          renderTag={Tag}
          inputValue={query}
          inputRef={inputRef}
          validate={validate}
          maxTags={maxContacts}
          tagDisplayProp='name'
          value={contactsArray}
          placeholder='Search or type email address...'
          onChangeInput={setQuery}
          onChange={(tags, changed) =>
            changed.forEach((tag) => deselectContact(tag.key, '', ''))
          }
        />
        <Alert show={showAlert && unsubscribedContact !== false}>
          <Icon className='fas fa-exclamation-triangle' />
          &nbsp;
          <span>
            `{unsubscribedContact !== false && unsubscribedContact.email}` has
            unsubscribed, please try a different email.
          </span>
        </Alert>
        {maxContacts > 1 && (
          <InputHelpBlock
            maxContacts={maxContacts}
            contactCount={contactCount}
          />
        )}
        <ButtonWrapper>
          <ActionButton
            className={'action-button-wrapper'}
            desktopText={actionText}
            mobileText={actionText}
            canGoToNextStep={canGoToNextStep}
            onClick={queryIsEmail ? partial(selectEmail, query) : null}
            isComplete={queryIsEmail ? true : null}
            disableHotkey={focusIndex != null}
          />
          {mode == TodoCreateMode.GROUP && (
            <Button
              disabled={isNextBtnActive}
              onClick={goToNextStep}
              className='mt-[5px]'
            >
              Next
            </Button>
          )}
        </ButtonWrapper>
        {isCurrent && (
          <SearchResults
            query={query}
            people={profiles}
            isLoading={isLoading}
            checkIfSelected={isContactSelected}
            select={partial(handleContactAction, selectContact)}
            deselect={partial(handleContactAction, deselectContact)}
          />
        )}
        <FormValues contacts={contactsArray} />
      </Step>
    </Context.Provider>
  );
}
